import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PipelineProgram, createProgram, updateProgram } from '../../services/pipelineService';

interface ProgramDialogProps {
  open: boolean;
  program: PipelineProgram | null;
  onClose: (success: boolean) => void;
}

interface FormData {
  name: string;
  display_name: string;
  description: string;
  setup_fee: string;
  monthly_fee: string;
  leads_per_week: string;
  is_active: boolean;
}

const ProgramDialog: React.FC<ProgramDialogProps> = ({ open, program, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    display_name: '',
    description: '',
    setup_fee: '0',
    monthly_fee: '0',
    leads_per_week: '0',
    is_active: true,
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (program) {
      setFormData({
        name: program.name,
        display_name: program.display_name,
        description: program.description || '',
        setup_fee: program.setup_fee.toString(),
        monthly_fee: program.monthly_fee.toString(),
        leads_per_week: program.leads_per_week.toString(),
        is_active: program.is_active,
      });
    } else {
      setFormData({
        name: '',
        display_name: '',
        description: '',
        setup_fee: '0',
        monthly_fee: '0',
        leads_per_week: '0',
        is_active: true,
      });
    }
    setErrors({});
    setError(null);
  }, [program, open]);

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Program name is required';
    }
    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required';
    }

    const setupFee = parseFloat(formData.setup_fee);
    if (isNaN(setupFee) || setupFee < 0) {
      newErrors.setup_fee = 'Setup fee must be a positive number';
    }

    const monthlyFee = parseFloat(formData.monthly_fee);
    if (isNaN(monthlyFee) || monthlyFee < 0) {
      newErrors.monthly_fee = 'Monthly fee must be a positive number';
    }

    const leadsPerWeek = parseInt(formData.leads_per_week, 10);
    if (isNaN(leadsPerWeek) || leadsPerWeek < 0) {
      newErrors.leads_per_week = 'Leads per week must be a positive integer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const programData = {
        name: formData.name.trim(),
        displayName: formData.display_name.trim(),
        description: formData.description.trim() || undefined,
        setupFee: parseFloat(formData.setup_fee),
        monthlyFee: parseFloat(formData.monthly_fee),
        leadsPerWeek: parseInt(formData.leads_per_week, 10),
        features: {},
        isActive: formData.is_active,
      };

      if (program) {
        await updateProgram(program.id, programData);
      } else {
        await createProgram(programData);
      }

      onClose(true);
    } catch (err: any) {
      console.error('Error saving program:', err);
      setError(
        err.response?.data?.message ||
        'Failed to save program. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {program ? 'Edit Program' : 'Create New Program'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Program Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name || 'Internal identifier (e.g., real_estate_leads)'}
              disabled={submitting}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Display Name"
              value={formData.display_name}
              onChange={handleChange('display_name')}
              error={!!errors.display_name}
              helperText={errors.display_name || 'User-facing name'}
              disabled={submitting}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={3}
              disabled={submitting}
              helperText="Optional program description"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Setup Fee"
              type="number"
              value={formData.setup_fee}
              onChange={handleChange('setup_fee')}
              error={!!errors.setup_fee}
              helperText={errors.setup_fee}
              disabled={submitting}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Monthly Fee"
              type="number"
              value={formData.monthly_fee}
              onChange={handleChange('monthly_fee')}
              error={!!errors.monthly_fee}
              helperText={errors.monthly_fee}
              disabled={submitting}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Leads Per Week"
              type="number"
              value={formData.leads_per_week}
              onChange={handleChange('leads_per_week')}
              error={!!errors.leads_per_week}
              helperText={errors.leads_per_week}
              disabled={submitting}
              inputProps={{ min: 0, step: 1 }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.is_active ? 'active' : 'inactive'}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'active' }))}
              disabled={submitting}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {program ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProgramDialog;
